import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { IncomeStreamExclusionReason } from 'src/app/models/Argyle';
import {
  BanksyncDetail,
  BanksyncSubStreamDetail,
  DraftIncomeSource,
  parseDraftDetail,
  PayFrequency,
  SaveDraftIncomeSourceRequest,
  StreamDecision,
} from 'src/app/models/income-wizard-workflow';
import { PanelDecision, PanelDraftState } from '../shared/stream-review-question-panel.component';

interface SubStreamState {
  draft: DraftIncomeSource;
  detail: BanksyncSubStreamDetail | null;
  panelState: PanelDraftState | null;
  decision: PanelDecision | null;
}

@Component({
  selector: 'app-stream-review-banksync',
  templateUrl: './stream-review-banksync.component.html',
  styleUrls: ['./stream-review-banksync.component.less'],
})
export class StreamReviewBanksyncComponent implements OnInit, OnChanges {
  @Input() screeningId!: number;
  @Input() applicantName = '';
  @Input() drafts: DraftIncomeSource[] = [];
  @Input() exclusionReasons: IncomeStreamExclusionReason[] = [];
  @Input() disabled = false;

  /** Emits one or more decisions when the processor clicks "Done with this account". */
  @Output() streamDecisions = new EventEmitter<StreamDecision[]>();
  /** Kept for exclude-entire-account (single decision). */
  @Output() streamDecision = new EventEmitter<StreamDecision>();

  subStreams: SubStreamState[] = [];
  showExcludeAccount = false;
  expandedTransactions: Record<number, boolean> = {};

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['drafts'] && !changes['drafts'].firstChange)) {
      this.resetState();
    }
  }

  ngOnInit(): void {
    this.resetState();
  }

  private resetState(): void {
    this.subStreams = (this.drafts ?? []).map((draft) => {
      const accountDetail = parseDraftDetail<BanksyncDetail>(draft);
      const subStreamId = draft.streamIdentifier.split(':').pop();
      const matchedDetail = accountDetail?.subStreams?.find((s) => s.id === subStreamId) ?? null;
      return {
        draft,
        detail: matchedDetail,
        panelState: draft.status !== 'Pending' ? {
          isExcluded: draft.isExcluded,
          incomeAmount: draft.incomeAmount,
          exclusionReasonId: draft.exclusionReasonId,
          notes: draft.notes,
        } : null,
        decision: null,
      };
    });
    this.showExcludeAccount = false;
    this.expandedTransactions = {};
  }

  get hasSubStreams(): boolean {
    return this.subStreams.length > 0;
  }

  get allDecided(): boolean {
    return this.subStreams.length > 0 && this.subStreams.every((s) => s.decision != null);
  }

  get includedCount(): number {
    return this.subStreams.filter((s) => s.decision?.action === 'include').length;
  }

  get totalIncludedMonthly(): number {
    return this.subStreams
      .filter((s) => s.decision?.action === 'include')
      .reduce((sum, s) => sum + ((s.decision as PanelDecision & { monthlyAmount: number }).monthlyAmount || 0), 0);
  }

  onSubStreamDecision(index: number, d: PanelDecision): void {
    if (index >= 0 && index < this.subStreams.length) {
      this.subStreams[index] = { ...this.subStreams[index], decision: d };
    }
  }

  clearSubStreamDecision(index: number): void {
    if (index >= 0 && index < this.subStreams.length) {
      this.subStreams[index] = { ...this.subStreams[index], decision: null };
    }
  }

  toggleTransactions(index: number): void {
    this.expandedTransactions[index] = !this.expandedTransactions[index];
  }

  isTransactionsExpanded(index: number): boolean {
    return !!this.expandedTransactions[index];
  }

  onDoneWithAccount(): void {
    if (this.disabled || !this.allDecided) {
      return;
    }
    const decisions: StreamDecision[] = [];

    for (const sub of this.subStreams) {
      const d = sub.decision!;
      const draft: SaveDraftIncomeSourceRequest = {
        draftId: sub.draft.id,
        wizardStateId: 0,
        screeningId: this.screeningId,
        streamType: sub.draft.streamType,
        subType: sub.draft.subType,
        streamIdentifier: sub.draft.streamIdentifier,
        employerName: sub.draft.employerName,
        incomeAmount: d.action === 'include' ? d.monthlyAmount : 0,
        payFrequency: PayFrequency.Monthly,
        suggestedAmount: sub.draft.suggestedAmount,
        notes: d.notes,
        isExcluded: d.action === 'exclude',
        exclusionReasonId: d.action === 'exclude' ? d.exclusionReasonId : null,
      };
      decisions.push({ action: d.action, draftSource: draft });
    }

    this.streamDecisions.emit(decisions);
  }

  onExcludeEntireAccount(): void {
    if (this.disabled) {
      return;
    }
    this.showExcludeAccount = true;
  }

  /** Called from the exclude-account panel. */
  onAccountExcludeDecision(d: PanelDecision): void {
    if (d.action === 'exclude') {
      // Exclude all sub-stream drafts at once
      const decisions: StreamDecision[] = this.subStreams.map((sub) => ({
        action: 'exclude' as const,
        draftSource: {
          draftId: sub.draft.id,
          wizardStateId: 0,
          screeningId: this.screeningId,
          streamType: sub.draft.streamType,
          subType: sub.draft.subType,
          streamIdentifier: sub.draft.streamIdentifier,
          employerName: sub.draft.employerName,
          incomeAmount: 0,
          payFrequency: PayFrequency.Monthly,
          suggestedAmount: sub.draft.suggestedAmount,
          notes: d.notes,
          isExcluded: true,
          exclusionReasonId: d.exclusionReasonId,
        },
      }));
      this.streamDecisions.emit(decisions);
    }
  }
}
